/**
 * O cartão de prontidão de go-live (9.2), fixo acima das abas da tela Configuração (11.2).
 *
 * Antes da virada alguém precisa olhar uma tela e saber **o que ainda falta configurar**. Este é
 * o único lugar do CRM onde isso existe: as quatro listas de domínio, um usuário ativo em cada
 * perfil, os números de WhatsApp conectados e o backup — o destino externo e,
 * desde o endurecimento do DW-56, o desfecho da última passada (ok/instante, lido do arquivo de
 * estado do agendador). Sem ele, dois desses fatos eram ilegíveis — o número de WhatsApp só
 * o dono vê (FR-71), e o backup só existia dentro do
 * contêiner que copia: uma passada falhando toda madrugada morria no stderr que ninguém olha.
 *
 * **Ele informa, e não bloqueia.** Nenhum item pendente impede ato nenhum do sistema, e este
 * cartão não tem botão que recuse coisa alguma. O que ele entrega, ao lado de cada pendência, é a
 * **consequência escrita** de deixá-la assim — porque a decisão de virar é humana, e ela precisa
 * do preço de cada item, não de um alarme.
 *
 * **Ele lê de quem é dono, e não de uma rota agregadora.** As listas vêm do `cadastro`, os
 * usuários e o backup da `identidade` e os números do `comunicacao` — quatro
 * consultas do SPA, cada uma recusada pelo próprio serviço (AD-25). Não há BFF neste repositório:
 * uma rota que chamasse quatro serviços seria padrão novo e daria a um serviço a chave dos outros
 * três. O preço deste desenho é a composição aqui; o ganho é que **uma fonte fora do ar degrada só
 * o item dela**, em vez de derrubar a página inteira.
 *
 * **Ele é só para o Admin, e aqui a exceção à convenção do repositório é do serviço.** Desde a 2.1
 * a regra é "esconder não é autorizar": as telas mostram as ações e o serviço recusa. Aqui três
 * das quatro rotas respondem `403` a todo mundo que não é Admin — a **leitura** inclusive —, então
 * um cartão montado para um Vendedor mostraria três "não foi possível verificar" e nada que ele
 * pudesse fazer. O componente devolve `null`, e as consultas próprias dele nem saem: elas nascem
 * `habilitada: false`, porque um hook roda **antes** de qualquer `return null`.
 *
 * **Ele é o primeiro cartão da tela**, e a posição é o argumento: antes de configurar qualquer
 * coisa, ver o que falta. Os seis cartões abaixo dele são justamente onde as pendências se
 * resolvem — as listas na 2.3, a régua na 3.6 — e um checklist no rodapé seria lido depois de a
 * pessoa já ter decidido sozinha por onde começar.
 *
 * **O desenho é o compacto do protótipo v2 (28/09/2026)**: "O que falta para começar", nota
 * "informa, não bloqueia", e os quatro itens numa linha — marca redonda, título e uma frase
 * curta (`resumo`). Ele nasceu como tabela de três colunas (decisão da 11.2) e o Gustavo pediu o
 * formato do protótipo em 28/09; a consequência escrita ficou, como segunda linha do pendente.
 *
 * **Paridade de celular (NFR-1).** Uma árvore só, e nenhuma `@media`: a grade de quatro empilha
 * pela classe que o `useEhCelular` do shell liga.
 */

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useOpcoesDeDominio } from '@/modulos/cadastro/api'
import { useProntidaoDoWhatsapp } from '@/modulos/comunicacao/api'
import { ehAdminDeFabrica, useProntidaoDoBackup, useUsuarios } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { useEhCelular } from '@/shell/Shell'
import {
  type Fonte,
  type ItemDeProntidao,
  type SituacaoDoItem,
  avaliarProntidao,
} from './avaliar-prontidao'
import './prontidao-de-go-live.css'

/** O nome acessível da seção — é ele que faz a `<section>` virar uma `region`. */
const ID_DO_TITULO = 'prontidao-de-go-live-titulo'

const CARREGANDO = 'Verificando o que falta configurar…'

/** As palavras do protótipo v2 (28/09/2026): o título e a nota à direita dele. */
export const TITULO = 'O que falta para começar'
export const NOTA = 'informa, não bloqueia'

/** O sinal da marca redonda de cada item (`.check__marca` do protótipo). */
const SINAIS: Record<SituacaoDoItem, string> = {
  resolvido: '✓',
  pendente: '!',
  indisponivel: '?',
}

const ROTULOS: Record<SituacaoDoItem, string> = {
  resolvido: 'Resolvido',
  pendente: 'Pendente',
  indisponivel: 'Não verificado',
}

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * O resultado de uma consulta como a avaliação o espera.
 *
 * Ela é o único ponto de tradução entre o React Query e a função pura, e existe para que a regra
 * de negócio não precise conhecer `isError`, `isLoading` nem `ErroDaApi`: o que atravessa é o
 * corpo **ou** a mensagem, e nada mais.
 *
 * **A recusa apaga o dado, e essa é a decisão desta função.** `consulta.data` **sobrevive** a uma
 * recusa no React Query: um refetch que falha deixa o corpo bem-sucedido anterior no cache, com
 * `isError` ligado ao lado. Para quase toda tela isso é uma bênção — mostrar o último dado bom em
 * vez de esvaziar a página. Aqui é o contrário: este cartão existe para dizer o que **agora**
 * falta configurar antes da virada, e um item pintado de "resolvido" a partir da resposta de dez
 * minutos atrás é a única mentira que ninguém tem como notar — a tela não dá nenhum sinal de que
 * o número é velho. "Não foi possível verificar", com a mensagem do serviço, é pior de olhar e
 * melhor de confiar.
 *
 * **Ela é exportada só para a suíte**, e isso é deliberado: a decisão acima é uma regra de
 * negócio de uma linha que nenhum caso de render alcança de fora — o cartão não tem mutação que
 * invalide as chaves dele, então "o cache tem dado e o refetch falhou" não é um estado que se
 * consiga montar clicando na tela. Sem a exportação, apagar o `isError ?` aqui deixaria a suíte
 * inteira verde e a tela mentindo.
 */
export function fonteDe<T>(consulta: {
  data: T | undefined
  isError: boolean
  error: unknown
}): Fonte<T> {
  return {
    dados: consulta.isError ? undefined : consulta.data,
    erro: consulta.isError ? mensagemDe(consulta.error) : null,
  }
}

/**
 * O texto de um detalhe, com os números em `.numero`.
 *
 * `.numero` é obrigatória em todo número exibido (UX-DR1, `src/estilos.css`), e os detalhes deste
 * cartão são frases — "6 de 9 usuários ativos", "sem usuário ativo: SDR". Envolver a frase inteira
 * jogaria o texto todo na mono; envolver só os dígitos, um a um, quebraria a data em três pedaços
 * com as barras fora. Por isso a expressão casa o **número inteiro**, separadores internos
 * incluídos.
 */
function DetalheComNumeros({ texto }: { texto: string }) {
  const pedacos = texto.split(/(\d+(?:[/:.,]\d+)*)/g)
  return (
    <>
      {pedacos.map((pedaco, indice) =>
        /^\d/.test(pedaco) ? (
          <span className="numero" key={`${String(indice)}-${pedaco}`}>
            {pedaco}
          </span>
        ) : (
          pedaco
        ),
      )}
    </>
  )
}

/**
 * A marca redonda do item — ✓ verde, ! vermelho, ? neutro (protótipo v2, `.check__marca`).
 *
 * O sinal é decoração: o estado legível fica no texto só-para-leitor ao lado, e a cor sai de
 * `data-situacao` no CSS, nunca de estilo inline.
 */
function MarcaDoItem({ situacao }: { situacao: SituacaoDoItem }) {
  return (
    <span className="prontidao__marca" data-situacao={situacao}>
      <span aria-hidden="true">{SINAIS[situacao]}</span>
      <span className="prontidao__so-para-leitor">{ROTULOS[situacao]}</span>
    </span>
  )
}

/**
 * Um item do cartão compacto: a marca, o título em negrito e a linha curta. O item pendente ganha
 * a **consequência escrita** como segunda linha, menor e apagada — ela é o que a 9.2 entrega, e
 * não sai da tela por causa do desenho compacto; o resolvido não a repete, porque não pede ação.
 * O indisponível traz, no mesmo lugar, a mensagem do serviço que caiu.
 */
function ItemDoCartao({ item }: { item: ItemDeProntidao }) {
  const segundaLinha =
    item.situacao === 'pendente'
      ? item.consequencia
      : item.situacao === 'indisponivel'
        ? item.detalhe
        : null
  return (
    <li className="prontidao__check">
      <MarcaDoItem situacao={item.situacao} />
      <div className="prontidao__texto">
        <span className="prontidao__nome">{item.titulo}</span>
        <span className="prontidao__resumo">
          <DetalheComNumeros texto={item.resumo} />
        </span>
        {segundaLinha === null ? null : (
          <span className="prontidao__consequencia">{segundaLinha}</span>
        )}
      </div>
    </li>
  )
}

export function ProntidaoDeGoLive() {
  const { data: sessao } = useSessao()
  const ehCelular = useEhCelular()

  /*
   * A leitura é do **admin de fábrica** (`perfil = admin` e `perfil_id` nulo, 11.1), e não de uma
   * permissão do catálogo: as três rotas são guardadas por `exigir_admin` do lado do serviço
   * (AD-18) — um personalizado derivado de Admin toma `403` nelas, e duas leituras diferentes fariam a
   * tela e o serviço discordarem sobre quem pode. Sem sessão o cartão não é montado — a guarda de
   * rota não deixa nada interno renderizar sem ela, e este ramo é a defesa contra o estado que ela
   * já impede.
   */
  const ehAdmin = sessao !== undefined && ehAdminDeFabrica(sessao)

  /*
   * As três consultas **próprias** deste cartão nascem fechadas para quem não é Admin: `habilitada`
   * fecha a porta antes da rede, e um hook roda antes de qualquer `return null`.
   *
   * A de baixo — as opções de domínio — não leva `habilitada`, e a ausência é deliberada: a rota
   * é aberta a todo perfil autenticado, e o cartão `ListasDeDominio` das abas já a pede para todo
   * mundo, com a mesma chave de cache.
   */
  const usuarios = useUsuarios({ habilitada: ehAdmin })
  const numeros = useProntidaoDoWhatsapp({ habilitada: ehAdmin })
  const backup = useProntidaoDoBackup({ habilitada: ehAdmin })
  const listas = useOpcoesDeDominio()

  if (!ehAdmin) {
    /*
     * Nem um cartão vazio, nem uma mensagem de "você não pode": três das quatro rotas respondem
     * `403` a Vendedor, SDR e Gestor comercial, então não há nada a mostrar e não há nada que eles
     * possam fazer aqui. A razão está no cabeçalho.
     */
    return null
  }

  const consultas = [listas, usuarios, numeros, backup]
  /*
   * "Carregando" enquanto **alguma** fonte ainda não assentou — nem corpo, nem recusa.
   *
   * A cadeia deste cartão é erro→carregando invertida em relação à dos vizinhos, e a inversão é a
   * promessa da história: uma fonte que falha **não** vira o erro do cartão, ela vira o estado de
   * um item. O único erro de cartão inteiro seria o de todas as quatro ao mesmo tempo — e mesmo
   * esse é mais útil desenhado como quatro itens "não verificado", cada um com a mensagem do seu
   * serviço, do que como uma frase só.
   */
  const carregando = consultas.some(
    (consulta) => consulta.data === undefined && !consulta.isError,
  )

  const itens = carregando
    ? []
    : avaliarProntidao({
        listas: fonteDe(listas),
        usuarios: fonteDe(usuarios),
        numeros: fonteDe(numeros),
        backup: fonteDe(backup),
      })
  const resolvidos = itens.filter((item) => item.situacao === 'resolvido').length

  return (
    <section className="prontidao" aria-labelledby={ID_DO_TITULO}>
      <div className="prontidao__cartao">
        <div className="prontidao__cabecalho">
          <h2 className="prontidao__titulo" id={ID_DO_TITULO}>
            {TITULO}
          </h2>
          <div className="prontidao__lado">
            {carregando ? null : (
              <p className="prontidao__placar" role="status">
                <span className="numero">{resolvidos}</span> de{' '}
                <span className="numero">{itens.length}</span> itens resolvidos
              </p>
            )}
            <span className="prontidao__nota">{NOTA}</span>
          </div>
        </div>

        {carregando ? (
          <p className="prontidao__carregando" role="status">
            {CARREGANDO}
          </p>
        ) : (
          /* A grade de quatro do protótipo; no celular ela empilha — pela árvore, e não por
             `@media` (a classe vem do `useEhCelular` do shell). */
          <ul className={`prontidao__checks${ehCelular ? ' prontidao__checks--empilhados' : ''}`}>
            {itens.map((item) => (
              <ItemDoCartao key={item.chave} item={item} />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
